// @ts-nocheck
import { useEffect, useState, useRef } from 'react';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { HighlightableSelect } from '@/components/ui/highlightable-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { HighlightEditor } from '@/components/ui/highlight-editor';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Slider } from '@/components/ui/slider';
import { Plus, Trash2 } from 'lucide-react';
import { ResponsiveImageUpload } from '../shared/responsive-image-upload';
import { useToast } from '@/hooks/use-toast';
import { uploadSiteSurveyPhoto } from '../shared/site-survey-photo-upload';

interface RoofInformationProps {
  form: UseFormReturn<any>;
  installationId?: number;
  surveyId?: number;
  onPhotoUploaded?: () => void;
}

export function RoofInformation({
  form,
  installationId,
  surveyId,
  onPhotoUploaded,
}: RoofInformationProps) {
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'roofs',
  });
  const [uploadingImages, setUploadingImages] = useState<Record<string, boolean>>({});
  const { toast } = useToast();
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const addRoof = () => {
    append({
      roofType: undefined,
      notes: '',
      pitch: undefined,
      roofImage1: '',
      roofImage2: '',
      roofImage3: '',
      membrane: undefined,
      membraneImage1: '',
      membraneImage2: '',
      existingFaults: undefined,
      optimisers: undefined,
    });
  };

  const isMobileDevice = () => {
    return (
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      (navigator.maxTouchPoints && navigator.maxTouchPoints > 2)
    );
  };

  const handleImageUpload = async (field: string, file: File) => {
    setUploadingImages((prev) => ({ ...prev, [field]: true }));

    try {
      await uploadSiteSurveyPhoto({
        form,
        field,
        file,
        surveyId,
        installationId,
        customerId: form.getValues('customerId'),
        description: `Roof ${field} photo`,
        toast,
        onPhotoUploaded,
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload Failed',
        description:
          error instanceof Error ? error.message : 'Failed to upload image. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setUploadingImages((prev) => ({ ...prev, [field]: false }));
    }
  };

  const handleFileSelect = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleImageUpload(field, file);
    }
    event.target.value = '';
  };

  const handleCameraCapture = (field: string) => {
    const input = fileInputRefs.current[field];
    if (input) {
      if (isMobileDevice()) {
        input.setAttribute('capture', 'environment');
      } else {
        input.removeAttribute('capture');
      }
      input.click();
    }
  };

  const triggerFileUpload = (field: string) => {
    const input = fileInputRefs.current[field];
    if (input) {
      input.removeAttribute('capture');
      input.click();
    }
  };

  const renderImageUpload = (fieldName: string, label: string) => {
    return (
      <ResponsiveImageUpload
        form={form}
        fieldName={fieldName}
        label={label}
        isUploading={uploadingImages[fieldName]}
        onCameraCapture={() => handleCameraCapture(fieldName)}
        onFileUpload={() => triggerFileUpload(fieldName)}
        onPhotoDeleted={onPhotoUploaded}
      />
    );
  };

  const liftOptions = Array.from({ length: 9 }, (_, i) => i + 1);

  const renderRoofFields = (index: number) => (
    <>
      <div className="grid gap-4 md:grid-cols-2">
        {/* Roof Type */}
        <FormField
          control={form.control}
          name={`roofs.${index}.roofType`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Roof Type</FormLabel>
              <FormControl>
                <HighlightableSelect
                  value={field.value || ''}
                  onValueChange={field.onChange}
                  placeholder="Select roof type"
                  options={[
                    { value: 'concrete_tile', label: 'Concrete Tile' },
                    { value: 'plain_tile', label: 'Plain Tile' },
                    { value: 'slate', label: 'Slate' },
                    { value: 'flat', label: 'Flat' },
                    { value: 'trap', label: 'Trap' },
                    { value: 'other', label: 'Other' },
                  ]}
                  fieldName={`roofs.${index}.roofType`}
                  form={form}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Pitch */}
        <FormField
          control={form.control}
          name={`roofs.${index}.pitch`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Pitch</FormLabel>
              <div className="space-y-3">
                <div className="flex items-center gap-4">
                  <FormControl className="flex-1">
                    <Slider
                      min={5}
                      max={55}
                      step={1}
                      value={[field.value || 25]}
                      onValueChange={(value) => field.onChange(value[0])}
                      className="flex-1"
                    />
                  </FormControl>
                  <Input
                    type="number"
                    min={5}
                    max={55}
                    value={field.value || ''}
                    onChange={(e) => {
                      const value = parseInt(e.target.value);
                      if (!isNaN(value) && value >= 5 && value <= 55) {
                        field.onChange(value);
                      } else if (e.target.value === '') {
                        field.onChange(undefined);
                      }
                    }}
                    className="w-20 text-center"
                  />
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>5</span>
                  <span>15</span>
                  <span>25</span>
                  <span>35</span>
                  <span>45</span>
                  <span>55</span>
                </div>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Notes */}
      <FormField
        control={form.control}
        name={`roofs.${index}.notes`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Notes</FormLabel>
            <FormControl>
              <HighlightEditor
                value={field.value || ''}
                onChange={field.onChange}
                placeholder="Enter any notes about the roof..."
                savedStatus={!!surveyId}
                minHeight="100px"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Roof Images */}
      <div className="space-y-2">
        <FormLabel>Roof Images</FormLabel>
        <div className="grid gap-4 md:grid-cols-3">
          {renderImageUpload(`roofs.${index}.roofImage1`, 'Image 1')}
          {renderImageUpload(`roofs.${index}.roofImage2`, 'Image 2')}
          {renderImageUpload(`roofs.${index}.roofImage3`, 'Image 3')}
        </div>
      </div>

      <Separator />

      {/* Membrane */}
      <FormField
        control={form.control}
        name={`roofs.${index}.membrane`}
        render={({ field }) => (
          <FormItem>
            <FormLabel>Membrane</FormLabel>
            <FormControl>
              <HighlightableSelect
                value={field.value || ''}
                onValueChange={field.onChange}
                placeholder="Select membrane condition"
                options={[
                  { value: 'none', label: 'None' },
                  { value: 'new', label: 'New' },
                  { value: 'good', label: 'Good' },
                  { value: 'poor', label: 'Poor' },
                ]}
                fieldName={`roofs.${index}.membrane`}
                form={form}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Membrane Images */}
      <div className="space-y-2">
        <FormLabel>Membrane Images</FormLabel>
        <div className="grid gap-4 md:grid-cols-2">
          {renderImageUpload(`roofs.${index}.membraneImage1`, 'Membrane Image 1')}
          {renderImageUpload(`roofs.${index}.membraneImage2`, 'Membrane Image 2')}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Existing Faults */}
        <FormField
          control={form.control}
          name={`roofs.${index}.existingFaults`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Existing Faults</FormLabel>
              <FormControl>
                <HighlightableSelect
                  value={field.value || ''}
                  onValueChange={field.onChange}
                  placeholder="Select fault status"
                  options={[
                    { value: 'client_acknowledgment', label: 'Client Acknowledgment' },
                    { value: 'none', label: 'None' },
                    { value: 'yes_required', label: 'Yes (Required)' },
                  ]}
                  fieldName={`roofs.${index}.existingFaults`}
                  form={form}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Optimisers */}
        <FormField
          control={form.control}
          name={`roofs.${index}.optimisers`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Optimisers</FormLabel>
              <FormControl>
                <HighlightableSelect
                  value={field.value || ''}
                  onValueChange={field.onChange}
                  placeholder="Select optimiser status"
                  options={[
                    { value: 'quoted', label: 'Quoted' },
                    { value: 'required', label: 'Required' },
                    { value: 'no', label: 'No' },
                  ]}
                  fieldName={`roofs.${index}.optimisers`}
                  form={form}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </>
  );

  // Ensure at least one roof exists
  useEffect(() => {
    if (fields.length === 0) {
      addRoof();
    }
  }, [fields.length, append]);

  return (
    <div className="space-y-6">
      {[
        'scaffoldPlanImage',
        ...fields.flatMap((_, i) => [
          `roofs.${i}.roofImage1`,
          `roofs.${i}.roofImage2`,
          `roofs.${i}.roofImage3`,
          `roofs.${i}.membraneImage1`,
          `roofs.${i}.membraneImage2`,
        ]),
      ].map((key) => (
        <input
          key={key}
          ref={(el) => (fileInputRefs.current[key] = el)}
          type="file"
          accept="image/*,.heic,.heif"
          style={{ display: 'none' }}
          onChange={handleFileSelect(key)}
        />
      ))}
      {/* Main Roof (Roof 1) - Always present */}
      <Card className="relative">
        <CardHeader>
          <CardTitle className="text-lg">Roof 1 (Primary Roof)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">{fields.length > 0 && renderRoofFields(0)}</CardContent>
      </Card>

      {/* Additional Roofs */}
      {fields.slice(1).map((field, index) => (
        <Card key={field.id} className="relative">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Additional Roof {index + 1}</CardTitle>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => remove(index + 1)}
                className="text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">{renderRoofFields(index + 1)}</CardContent>
        </Card>
      ))}

      {/* Add Additional Roof Button */}
      <Button type="button" variant="outline" onClick={addRoof} className="w-full">
        <Plus className="h-4 w-4 mr-2" />
        Add Additional Roof
      </Button>

      <Separator className="my-6" />

      {/* Scope of Works - Roof Work */}
      <FormField
        control={form.control}
        name="scopeOfWorksRoof"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Scope of Works - Roof Work</FormLabel>
            <FormControl>
              <HighlightEditor
                value={field.value || ''}
                onChange={field.onChange}
                placeholder="Enter scope of works for roof..."
                savedStatus={!!surveyId}
                minHeight="200px"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <Separator className="my-6" />

      {/* Scaffolding Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Scaffolding</h3>

        <div className="grid gap-4 md:grid-cols-2">
          {/* Scaffolding Required */}
          <FormField
            control={form.control}
            name="scaffolding"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Scaffolding</FormLabel>
                <FormControl>
                  <HighlightableSelect
                    value={field.value || ''}
                    onValueChange={field.onChange}
                    placeholder="Select scaffolding requirement"
                    options={[
                      { value: 'required', label: 'Required' },
                      { value: 'not_required', label: 'Not Required' },
                      { value: 'domelec_tower', label: 'Domelec Tower' },
                    ]}
                    fieldName="scaffolding"
                    form={form}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Width */}
          <FormField
            control={form.control}
            name="scaffoldingWidth"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Width (meters)</FormLabel>
                <div className="space-y-3">
                  <div className="flex items-center gap-4">
                    <FormControl className="flex-1">
                      <Slider
                        min={3}
                        max={50}
                        step={0.1}
                        value={[field.value || 10]}
                        onValueChange={(value) => field.onChange(value[0])}
                        className="flex-1"
                      />
                    </FormControl>
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        min={3}
                        max={50}
                        step={0.1}
                        value={field.value || ''}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value);
                          if (!isNaN(value) && value >= 3 && value <= 50) {
                            field.onChange(value);
                          } else if (e.target.value === '') {
                            field.onChange(undefined);
                          }
                        }}
                        className="w-20 text-center"
                      />
                      <span className="text-sm text-muted-foreground">m</span>
                    </div>
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>3m</span>
                    <span>10m</span>
                    <span>20m</span>
                    <span>30m</span>
                    <span>40m</span>
                    <span>50m</span>
                  </div>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Lifts */}
          <FormField
            control={form.control}
            name="scaffoldingLifts"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Lifts</FormLabel>
                <FormControl>
                  <HighlightableSelect
                    value={field.value?.toString() || ''}
                    onValueChange={(value) => field.onChange(parseInt(value))}
                    placeholder="Select number of lifts"
                    options={liftOptions.map((lift) => ({
                      value: lift.toString(),
                      label: `${lift} ${lift === 1 ? '(Bungalow)' : lift === 2 ? '(2 Storey House)' : ''}`.trim(),
                    }))}
                    fieldName="scaffoldingLifts"
                    form={form}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Scaffolding Notes */}
        <FormField
          control={form.control}
          name="scaffoldingNotes"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Scaffolding Notes</FormLabel>
              <FormControl>
                <HighlightEditor
                  value={field.value || ''}
                  onChange={field.onChange}
                  placeholder="Enter scaffolding notes..."
                  savedStatus={!!surveyId}
                  minHeight="100px"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Scaffold Plan Image */}
        {renderImageUpload('scaffoldPlanImage', 'Scaffold Plan Image')}
      </div>
    </div>
  );
}
